import { Fragment, useEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * A button that opens a short list of actions — the version line's "…" and the "•••" at the
 * end of every rule row. Closes on an outside click, on Escape and when an item is picked.
 *
 * The row variant opens with fixed positioning measured from its button: the rule table
 * sits in a horizontally scrolling wrapper, which would otherwise clip the menu of the last
 * rows. Scrolling the page closes it rather than leaving it floating away from its row.
 */

export interface ActionMenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /** A line above this item — sets a release-type action apart from the everyday ones (UX review P1-5). */
  separated?: boolean;
  /** Why a disabled item is unavailable ("switch the set off first"), as its tooltip (QA-R-17). */
  hint?: string;
  /** A tooltip for the item, disabled or not (QA-A-07 uses it on a disabled Reset password). */
  title?: string;
}

export function ActionMenu({
  label,
  items,
  variant = 'page',
}: {
  /** The button's accessible name. */
  label: string;
  items: ActionMenuItem[];
  variant?: 'page' | 'row';
}) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<CSSProperties | undefined>(undefined);
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    if (variant === 'row') {
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onScroll);
    }
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, variant]);

  const toggle = () => {
    if (!open && variant === 'row') {
      const r = button.current?.getBoundingClientRect();
      setPlace(r ? { position: 'fixed', top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) } : undefined);
    }
    setOpen((v) => !v);
  };

  return (
    <span className={variant === 'row' ? 'more row' : 'more'} ref={root}>
      <button
        ref={button}
        type="button"
        className={variant === 'row' ? 'row-menu-btn' : 'btn small more-btn'}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        {/* One glyph for "more" everywhere (design review 30 Sep): the version line used a tiny '…'. */}
        •••
      </button>
      {open && (
        <div className="more-menu" role="menu" style={place}>
          {items.map((item) => (
            <Fragment key={item.label}>
              {item.separated && <div role="separator" />}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                title={item.disabled ? (item.hint ?? item.title) : item.title}
                onClick={() => {
                  setOpen(false);
                  // Focus back on the button first: a dialog the item opens returns focus here when it closes.
                  button.current?.focus();
                  item.onSelect();
                }}
              >
                {item.label}
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </span>
  );
}
